import { isAfter, subHours } from 'date-fns';
import {
  DeliveryMethod,
  Message,
  ParticipantDetails,
} from 'src/models/conversation/_types';

import { User } from '../user/_types';

/** Stable default — a fresh array literal each render resets SMS via NewMessageInput's sync effect. */
export const DEFAULT_MESSAGE_DELIVERY: DeliveryMethod[] = ['in-app', 'email'];

export const getReceivingParticipant = (
  participants: ParticipantDetails[],
  currentUserId: User['id'],
  trainerIds: User['id'][]
): ParticipantDetails | undefined => {
  if (participants.length === 0) {
    return undefined;
  }

  if (participants.length === 1) {
    return participants[0];
  }

  const nonTrainerReceiver = participants.find((participant) => {
    return (
      !trainerIds.includes(participant.id) && participant.id !== currentUserId
    );
  });

  if (nonTrainerReceiver) {
    return nonTrainerReceiver;
  }

  return (
    participants.find((participant) => participant.id !== currentUserId) ||
    participants[0]
  );
};

export const getSendingParticipant = (
  participants: ParticipantDetails[],
  currentUserId: User['id'],
  trainerIds: User['id'][]
): ParticipantDetails | undefined => {
  if (participants.length === 0) {
    return undefined;
  }

  if (participants.length === 1) {
    return participants[0];
  }

  const trainerSender = participants.find(
    (participant) =>
      trainerIds.includes(participant.id) && participant.id === currentUserId
  );

  if (trainerSender) {
    return trainerSender;
  }

  return (
    participants.find((participant) => participant.id === currentUserId) ||
    participants[0]
  );
};

/**
 * Check if a message can be edited (within 1 hour of creation and sent by current user)
 */
export const isEditable = (
  message: Message,
  currentUserId: User['id']
): boolean => {
  // Must be sent by current user
  if (message.senderId !== currentUserId) {
    return false;
  }

  // SMS messages should not be editable
  if (isMessageFromSMS(message)) {
    return false;
  }

  // Must be within 1 hour of creation
  const oneHourAgo = subHours(new Date(), 1);
  return isAfter(message.createdAt, oneHourAgo);
};

/**
 * Check if a message has been edited
 */
export const wasEdited = (message: Message): boolean => {
  return Boolean(message.editedAt);
};

/* Checks if a message was received via SMS
 * @param message - The message to check
 * @returns true if the message was sent via SMS, false otherwise
 */
export const isMessageFromSMS = (message: Message): boolean => {
  return message.sourceType === 'sms';
};
